import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
} from "@mui/material";
import { getCookie, getHeaders } from "../../utils/api";
import envConfig from "../../config";

const TestErrors = () => {
  const [lastResponse, setLastResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const updateResponse = (testName, response, error = null) => {
    const timestamp = new Date().toISOString();

    let headers = {};
    if (response && response.headers) {
      try {
        if (response.headers.entries) {
          headers = Object.fromEntries(response.headers.entries());
        } else if (response.headers.forEach) {
          response.headers.forEach((value, key) => {
            headers[key] = value;
          });
        }
      } catch (e) {
        console.log("Could not parse headers:", e);
        headers = { error: "Could not parse headers" };
      }
    }

    setLastResponse({
      testName,
      timestamp,
      response: response
        ? {
            status: response.status,
            statusText: response.statusText,
            headers: headers,
            url: response.url,
            body: response.body,
          }
        : null,
      error: error ? error.message : null,
      raw: response,
    });
  };

  const makeRequest = async (testName, url, options = {}) => {
    setIsLoading(true);
    console.log(`🧪 Test: ${testName}`, { url, options });

    try {
      // Let interceptor handle errors, but we still need try-catch for loading state
      const response = await fetch(url, options);
      const text = await response.text();

      updateResponse(testName, {
        ...response,
        body: text,
      });

      console.log(`🧪 Test ${testName} Response:`, {
        status: response.status,
        body: text.substring(0, 200),
      });
    } catch (error) {
      // Still let interceptor handle the error, just update our loading state
      updateResponse(testName, null, error);
      console.log(`🧪 Test ${testName} caught error:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const testScenarios = [
    {
      category: "Internal API Calls",
      tests: [
        {
          name: "Get Business Activities",
          action: () =>
            makeRequest(
              "Business Activities",
              `https://${envConfig.apiDev1Exacc}/api/v1/en-au/business-details/get-business-main-activities-async`,
              {
                method: "GET",
                headers: getHeaders(),
              }
            ),
        },
        {
          name: "Get Business Types",
          action: () =>
            makeRequest(
              "Business Types",
              `https://${envConfig.apiDev1}/api/v3/en-au/businesses/get-types?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "GET",
                headers: getHeaders(),
              }
            ),
        },
        {
          name: "Get Current Global User",
          action: () =>
            makeRequest(
              "Current Global User",
              `https://${envConfig.apiDev1Exacc}/api/v1/en-au/global-users/get-current-global-user`,
              {
                method: "GET",
                headers: getHeaders(),
              }
            ),
        },
      ],
    },
    {
      category: "Internal API Calls",
      tests: [
        {
          name: "Get User Databases",
          action: () =>
            makeRequest(
              "User Databases",
              `https://${envConfig.apiDev1}/api/v5/en-au/databases/get-user-databases-async?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "POST",
                headers: getHeaders(),
                body: JSON.stringify({
                  LoginSecured: getCookie("loginSecuredHash") || "",
                  UserId: getCookie("userId") || 0,
                  DatabaseId: getCookie("databaseId") || 0,
                }),
              }
            ),
        },
        {
          name: "Get Pending Invitations",
          action: () =>
            makeRequest(
              "Pending Invitations",
              `https://${envConfig.apiDev1}/api/v3/en-au/databases/get-pending-invitations-with-access-requests?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "GET",
                headers: getHeaders(),
              }
            ),
        },
        {
          name: "Get Mobile Dashboard Data",
          action: () =>
            makeRequest(
              "Mobile Dashboard",
              `https://${envConfig.apiDev2}/api/front-end/get-mobile-dashboard-data?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "POST",
                headers: getHeaders(),
                body: JSON.stringify({
                  LinkA: getCookie("databaseId") || 0,
                  DatabaseId: getCookie("databaseId") || 0,
                }),
              }
            ),
        },
      ],
    },
    {
      category: "Internal API - Invalid Auth/Data",
      tests: [
        {
          name: "No API Key",
          action: () =>
            makeRequest(
              "No API Key Test",
              `https://${envConfig.apiDev1}/api/v3/en-au/databases/get-pending-invitations-with-access-requests?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  "olivs-api-real-ip": "31.42.0.186",
                  "olivs-api-computer-name": "BTM-WS2019",

                  // No api-key header
                },
              }
            ),
        },
        {
          name: "Invalid API Key",
          action: () =>
            makeRequest(
              "Invalid API Key",
              `https://${envConfig.apiDev1}/api/v5/en-au/databases/get-user-databases-async?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  "olivs-api-real-ip": "31.42.0.186",
                  "olivs-api-computer-name": "BTM-WS2019",

                  "api-key": "INVALID-KEY-12345",
                },
                body: JSON.stringify({
                  LoginSecured: "invalid-hash",
                  UserId: 0,
                  DatabaseId: 0,
                }),
              }
            ),
        },
        {
          name: "Missing refresh key (negative test)",
          action: () => {
            const h = {
              ...getHeaders(),
            };
            delete h["refresh-key"];
            return makeRequest(
              "Missing refresh key (negative test)",
              `https://${envConfig.apiDev1}/api/v3/en-au/databases/get-pending-invitations-with-access-requests?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "GET",
                headers: h,
              }
            );
          },
        },
        {
          name: "Invalid POST Data",
          action: () =>
            makeRequest(
              "Invalid POST Data",
              `https://${envConfig.apiDev2}/api/front-end/get-mobile-dashboard-data?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "POST",
                headers: getHeaders(),
                body: JSON.stringify({
                  InvalidField: "should cause error",
                  WrongData: null,
                }),
              }
            ),
        },
        {
          name: "Create Business File - Empty Payload",
          action: () =>
            makeRequest(
              "Create Business File Empty",
              `https://${envConfig.apiDev1}/api/v6/en-au/databases/create-new-business-file-async?BaseHostURL=${envConfig.loginUrl}`,
              {
                method: "POST",
                headers: getHeaders(),
                body: JSON.stringify({}),
              }
            ),
        },
      ],
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom align="center">
        API Interceptor Test Page
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        color="text.secondary"
        gutterBottom
      >
        Test various API scenarios to see how the interceptor handles errors,
        authentication, and responses
      </Typography>
      <Grid container spacing={3}>
        {/* Test Buttons */}
        <Grid item xs={12} md={8}>
          {testScenarios.map((category, categoryIndex) => (
            <Card key={categoryIndex} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {category.category}
                </Typography>
                <Grid container spacing={1}>
                  {category.tests.map((test, testIndex) => (
                    <Grid item key={testIndex}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={test.action}
                        disabled={isLoading}
                        sx={{ mb: 1 }}
                        data-testid="test-errors--Button-0">
                        {test.name}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Clear Response
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setLastResponse(null)}
                data-testid="test-errors--Button-1">
                Clear Response Box
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Response Display */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, position: "sticky", top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Latest Response
              {isLoading && (
                <Chip
                  label="Loading..."
                  color="info"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>

            {lastResponse ? (
              <Box>
                <Typography variant="subtitle2" color="primary">
                  {lastResponse.testName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  gutterBottom
                >
                  {lastResponse.timestamp}
                </Typography>

                <Divider sx={{ my: 1 }} />

                {lastResponse.error ? (
                  <Box>
                    <Chip label="Error" color="error" size="small" />
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {lastResponse.error}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Chip
                      label={`${lastResponse.response?.status} ${lastResponse.response?.statusText}`}
                      color={
                        lastResponse.response?.status < 400
                          ? "success"
                          : "error"
                      }
                      size="small"
                    />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>URL:</strong> {lastResponse.response?.url}
                    </Typography>

                    {lastResponse.response?.headers &&
                      Object.keys(lastResponse.response.headers).length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Headers:</strong>
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              fontSize: "0.75rem",
                              overflow: "auto",
                              maxHeight: 100,
                            }}
                          >
                            {JSON.stringify(
                              lastResponse.response.headers,
                              null,
                              2
                            )}
                          </Box>
                        </Box>
                      )}

                    {lastResponse.response?.body && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Response Body:</strong>
                        </Typography>
                        <TextField
                          multiline
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={lastResponse.response.body}
                          InputProps={{
                            readOnly: true,
                            sx: { fontSize: "0.75rem" },
                          }}
                          sx={{ mt: 0.5 }}
                          maxRows={10}
                          data-testid="test-errors--TextField-0" />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No response yet. Click any test button to see the result here.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TestErrors;
